/**
 * Phase 4C — helper DISPLAY batas waktu pembayaran manual.
 * Source of truth = order.payment_deadline (server). `server_now` hanya untuk offset jam perangkat.
 * Tidak ada business decision di sini: backend tetap otoritas untuk klaim/expired/extension/timeout.
 */
import { MANUAL_PAYMENT_METHODS } from "@/lib/orderCancellation";

export const PAYMENT_DEADLINE_WARNING_MS = 10 * 60 * 1000;
export const STOPPED_PAYMENT_STATUSES = ["pending_verification", "verified", "paid"];

/** server_now - client_now (ms). Tidak valid → 0. */
export function serverOffsetMs(serverNowIso, clientNowMs = Date.now()) {
  const t = Date.parse(serverNowIso || "");
  return Number.isFinite(t) ? t - clientNowMs : 0;
}

export function hasDeadlineLifecycle(order) {
  return !!(order && order.payment_deadline && MANUAL_PAYMENT_METHODS.includes(order.payment_method));
}

/** "none" | "stopped" | "paused" | "active" | "warning" | "expired" */
export function deadlinePhase(order, nowMs) {
  if (!hasDeadlineLifecycle(order)) return "none";
  if (order.status !== "pending_payment") return "stopped";
  if (STOPPED_PAYMENT_STATUSES.includes(order.payment_status)) return "stopped";
  if ((order.cancellation || {}).request_status === "REQUESTED") return "paused";
  const remaining = remainingMs(order, nowMs);
  if (!(remaining > 0)) return "expired";
  return remaining <= PAYMENT_DEADLINE_WARNING_MS ? "warning" : "active";
}

export function remainingMs(order, nowMs) {
  const d = Date.parse((order && order.payment_deadline) || "");
  if (!Number.isFinite(d)) return 0;
  return Math.max(0, d - nowMs);
}

/** 3599000 → "59:59"; ≥ 1 jam → "1:00:00". */
export function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  const mm = String(m).padStart(2, "0"), ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** "20:42" (jam lokal perangkat, tampilan absolut untuk list). */
export function formatDeadlineClock(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
}

export const CLAIMABLE_PAYMENT_STATUSES = [undefined, null, "rejected"];

/** Mirror backend extension_conflict (tanpa flag — flag dicek via useIsFeatureEnabled + backend). */
export function canExtendDeadline(order) {
  if (!order || !hasDeadlineLifecycle(order)) return false;
  if ((order.cancellation || {}).request_status !== "REQUESTED") return false;
  if (order.status !== "pending_payment") return false;
  if (!CLAIMABLE_PAYMENT_STATUSES.includes(order.payment_status)) return false;
  return (order.payment_deadline_extension_count || 0) < 1;
}

/** Deadline sudah lewat / < 10 menit relatif ke nowMs → seller perlu peringatan sebelum reject. */
export function deadlineNearOrPast(order, nowMs) {
  if (!hasDeadlineLifecycle(order)) return false;
  return remainingMs(order, nowMs) <= PAYMENT_DEADLINE_WARNING_MS;
}

/** Visibilitas "Sudah Membayar" (mirror claim_conflict backend + fase deadline display). */
export function canClaimPayment(order, phase) {
  if (!order || !MANUAL_PAYMENT_METHODS.includes(order.payment_method)) return false;
  if (order.status !== "pending_payment") return false;
  if (["pending_verification", "verified", "paid"].includes(order.payment_status)) return false;
  if ((order.cancellation || {}).request_status === "REQUESTED") return false;
  return !["expired", "paused"].includes(phase);
}

/** Upload bukti = aksi pembayaran: ikut aturan deadline & REQUESTED yang sama (Phase 4A safety di server). */
export function canUploadProof(order, phase) {
  if (!order || !MANUAL_PAYMENT_METHODS.includes(order.payment_method)) return false;
  if (order.status !== "pending_payment") return false;
  if (["verified", "paid"].includes(order.payment_status)) return false;
  if ((order.cancellation || {}).request_status === "REQUESTED") return false;
  return !["expired", "paused"].includes(phase);
}
